import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import FlexLib from '@flex/lib';
import classNames from 'classnames';
import invoiceCSS from '../Invoice.module.scss';
import { Table, TableHead, TableBody, TableRow, TableCell, Autocomplete, TextField } from '@mui/material';
import { Cancel } from '@mui/icons-material';
import AmountTextField from '../../../components/FeeDisplay/AmountTextField';
const { calculateProductLineAmount } = FlexLib.utils.feeService;

const ProductRow = (props) => {
  const handleDescriptionChange = (event) => {
    const newProduct = { ...props.product, description: event?.target?.value };
    props.handleUpdateProductLine(newProduct, props.index);
  };

  const handleUnitCostChange = (newUnitCost = '0.00') => {
    const newLineTotal = calculateProductLineAmount(newUnitCost, props.product.quantity);
    const newProduct = { ...props.product, unitCost: newUnitCost, amount: newLineTotal };
    props.handleUpdateProductLine(newProduct, props.index);
  };

  const handleQuantityChange = (event) => {
    const newQuantity = Number.isNaN(Number.parseInt(event?.target?.value)) ? 0 : Number.parseInt(event.target.value);
    const newLineTotal = calculateProductLineAmount(props.product.unitCost, newQuantity);
    const newProduct = { ...props.product, quantity: newQuantity, amount: newLineTotal };
    props.handleUpdateProductLine(newProduct, props.index);
  };

  return (
    <TableRow id={props.index}>
      <TableCell>
        <div data-cy="product-dropdown">
          <Autocomplete
            disabled={!!props.product?._id}
            options={props.formattedProductList}
            onChange={props.handleSelectProduct}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            value={{
              value: props.product?._id,
              label: props.product?.name?.charAt(0)?.toUpperCase() + props.product?.name?.slice(1),
              _id: props.product?._id,
            }}
            renderInput={(params) => <TextField {...params} variant="standard" />}
            isOptionEqualToValue={(option, value) => !value.value || option.value === value.value}
          />
        </div>
      </TableCell>
      <TableCell>
        <div data-cy="product-description">
          <TextField
            value={props.product?.description}
            onChange={handleDescriptionChange}
            fullWidth
            variant="standard"
          />
        </div>
      </TableCell>
      <TableCell>
        <div data-cy="product-unit-cost">
          <AmountTextField
            amount={props.product?.unitCost}
            setAmount={handleUnitCostChange}
            label=""
            variant="standard"
            fullWidth
          />
        </div>
      </TableCell>
      <TableCell>
        <div data-cy="product-quantity">
          <TextField
            type="number"
            value={props.product?.quantity}
            onChange={handleQuantityChange}
            fullWidth
            variant="standard"
          />
        </div>
      </TableCell>
      <TableCell>
        <div data-cy="product-amount">
          <AmountTextField disabled amount={props.product?.amount} label="" variant="standard" fullWidth />
        </div>
      </TableCell>
      <TableCell align="center">
        {props.product?._id && <Cancel onClick={() => props.handleRemoveProductLine(props.index)} />}
      </TableCell>
    </TableRow>
  );
};

ProductRow.propTypes = {
  index: PropTypes.number,
  formattedProductList: PropTypes.array,
  handleSelectProduct: PropTypes.func,
  handleRemoveProductLine: PropTypes.func,
  handleUpdateProductLine: PropTypes.func,
  product: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    amount: PropTypes.string,
    unitCost: PropTypes.string,
    quantity: PropTypes.number,
    description: PropTypes.string,
    status: PropTypes.bool,
  }),
};

const ProductTable = (props) => {
  const { products, setProducts } = props;
  const [formattedProductList, setFormattedProductList] = useState([]); // product offerings
  const [displayProductRows, setDisplayProductRows] = useState([]);

  useEffect(() => {
    formatProductList(props.productList);
  }, [props.productList]);

  useEffect(() => {
    const productRows =
      products?.map((product, key) => {
        return (
          <ProductRow
            key={key}
            index={key}
            product={product}
            formattedProductList={formattedProductList}
            handleSelectProduct={handleSelectProductFromList}
            handleRemoveProductLine={handleRemoveProductLine}
            handleUpdateProductLine={handleUpdateProductLine}
          />
        );
      }) ?? [];

    const lastProduct = products?.slice(-1);
    // if last product is not an empty product, add it to the end of the array
    if (!products || products?.length === 0 || lastProduct[0]?._id) {
      const emptyProduct = {
        _id: null,
        name: '',
        description: '',
        unitCost: '',
        amount: '',
        quantity: 0,
        status: false,
      };

      const emptyProductRow = (
        <ProductRow
          key={-1}
          index={-1}
          product={emptyProduct}
          formattedProductList={formattedProductList}
          handleSelectProduct={handleSelectProductFromList}
          handleRemoveProductLine={handleRemoveProductLine}
          handleUpdateProductLine={handleUpdateProductLine}
        />
      );
      productRows.push(emptyProductRow);
    }
    setDisplayProductRows(productRows);
  }, [products, formattedProductList]);

  const formatProductList = (productList) => {
    if (productList?.length > 0) {
      const filteredProductList = productList?.filter((item) => item.status === true);
      const formattedProductList = filteredProductList.map((item) => {
        const name = item.name?.substr(0, 15);
        const formatted = {
          value: item._id,
          label: name?.charAt(0)?.toUpperCase() + name?.slice(1),
          _id: item._id,
          name: item.name.substr(0, 22),
          unitCost: item.amount?.toFixed(2),
          description: item.description,
          status: item.status,
        };
        return formatted;
      });
      setFormattedProductList(formattedProductList);
    } else {
      setFormattedProductList([]);
    }
  };

  const handleSelectProductFromList = (_event, value) => {
    if (value?.value) {
      const existingProducts = products ?? [];
      const updatedProducts = [
        ...existingProducts, // slice off empty product line
        {
          _id: value._id,
          name: value.name,
          description: value.description,
          unitCost: value.unitCost,
          amount: value.unitCost,
          quantity: 1,
          status: value.status,
        },
      ];

      setProducts(updatedProducts);
    }
  };

  const handleRemoveProductLine = (index) => {
    const values = [...products];
    values.splice(index, 1);
    setProducts(values);
  };

  const handleUpdateProductLine = (newProduct, index) => {
    const values = [...products];
    values[index] = newProduct;
    setProducts(values);
  };

  return (
    <Table
      aria-label="simple table"
      className={classNames(['table', 'dashboard-table', 'invoice-table', 'product-table'])}
    >
      <TableHead>
        <TableRow>
          <TableCell align="right" classes={invoiceCSS.itemHead}>
            Item
          </TableCell>
          <TableCell align="right" sx={{ width: '60%', 'text-align': 'left' }}>
            Description
          </TableCell>
          <TableCell align="right" sx={{ 'white-space': 'nowrap' }}>
            Unit Cost
          </TableCell>
          <TableCell align="right" sx={{ 'white-space': 'nowrap' }}>
            Quantity
          </TableCell>
          <TableCell align="right" sx={{ 'white-space': 'nowrap' }}>
            Line Total
          </TableCell>
          <TableCell align="right" sx={{ 'white-space': 'normal' }}>
            <Cancel sx={{ visibility: 'hidden !important' }} /> {/* Correct amount of space saver */}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>{displayProductRows}</TableBody>
    </Table>
  );
};

ProductTable.propTypes = {
  products: PropTypes.array,
  productList: PropTypes.array, // product offering list
  setProducts: PropTypes.func,
};

export default ProductTable;
